// ABOUTME: Comprehensive hook for SharePoint API operations and state management
// ABOUTME: Handles browse, search, item fetching with infinite scroll integration

import { useCallback, useRef, useState } from 'react';

import { browserItems, generateNodes, getItems, searchItems } from '../api/sharePointApi';
import type {
    IApiError,
    IBrowseRequest,
    IGenerateNodesRequest,
    IGenerateNodesResult,
    IItemBreadcums,
    IItemsRequest,
    ISearchRequest,
    ISharePointItem,
} from '../api/types';
import { useInfiniteScrollState } from './useInfiniteScrollState';

type IUseSharePointApiProps = {
    readonly hierarchyId: string;
    readonly pageSize?: number;
};

type IHookState = {
    // Data
    readonly files: readonly ISharePointItem[];
    readonly searchResults: readonly ISharePointItem[];
    readonly breadcrumbs: readonly IItemBreadcums[];
    readonly driveItemId?: string;
    readonly totalCount?: number;

    // States
    readonly loading: boolean;
    readonly loadingMore: boolean;
    readonly loadingMoreSearch: boolean;
    readonly error: string | null;

    // Pagination
    readonly nextLink?: string;
    readonly hasMore: boolean;
    readonly searchNextLink?: string;
    readonly searchHasMore: boolean;

    // Actions
    readonly browserItems: (
        driveItemId: string,
        isLoadMore?: boolean,
        customNextLink?: string,
        explicitPageSize?: number,
    ) => Promise<void>;
    readonly searchItems: (
        query: string,
        isLoadMore?: boolean,
        customNextLink?: string,
        explicitPageSize?: number,
    ) => Promise<void>;
    readonly fetchItems: (itemIds: string[]) => Promise<ISharePointItem[]>;
    readonly generateNodes: (
        hierarchyId: string,
        items: ISharePointItem[],
    ) => Promise<IGenerateNodesResult>;

    // Navigation
    readonly handleFolderOpen: (folder: ISharePointItem) => void;
    readonly handleBreadcrumbClick: (index: number) => void;

    // Infinite scroll
    readonly browseInfiniteScrollState: ReturnType<typeof useInfiniteScrollState>;
    readonly searchInfiniteScrollState: ReturnType<typeof useInfiniteScrollState>;

    // Utility
    readonly reset: () => void;
};

export const useSharePointApi = ({
    hierarchyId,
    pageSize = 25,
}: IUseSharePointApiProps): IHookState => {
    // Core state
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loadingMoreSearch, setLoadingMoreSearch] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Data state
    const [files, setFiles] = useState<IHookState['files']>([]);
    const [searchResults, setSearchResults] = useState<IHookState['searchResults']>([]);
    const [breadcrumbs, setBreadcrumbs] = useState<IHookState['breadcrumbs']>([]);
    const [driveItemId, setDriveItemId] = useState<IHookState['driveItemId']>();
    const [totalCount, setTotalCount] = useState<IHookState['totalCount']>();

    // Pagination state
    const [nextLink, setNextLink] = useState<IHookState['nextLink']>();
    const [hasMore, setHasMore] = useState<IHookState['hasMore']>(false);
    const [searchNextLink, setSearchNextLink] = useState<IHookState['searchNextLink']>();
    const [searchHasMore, setSearchHasMore] = useState<IHookState['searchHasMore']>(false);

    // Infinite scroll states
    const browseInfiniteScrollState = useInfiniteScrollState();
    const searchInfiniteScrollState = useInfiniteScrollState();

    // Refs to maintain reference equality
    const pageSizeRef = useRef(pageSize);
    pageSizeRef.current = pageSize;

    const browserItemsCallback = useCallback<IHookState['browserItems']>(
        async (dItemId, isLoadMore, customNextLink, explicitPageSize) => {
            const currentPageSize = explicitPageSize ?? pageSizeRef.current;

            if (isLoadMore) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }
            setError(null);

            try {
                const params: IBrowseRequest = {
                    hierarchyId,
                    driveItemId: dItemId,
                    pageSize: currentPageSize,
                    nextLink: customNextLink,
                };

                const response = await browserItems(params);

                if (response.data) {
                    if (isLoadMore) {
                        // Append to existing items
                        setFiles(prev => [...prev, ...response.data!.items]);
                    } else {
                        // Replace items
                        setFiles(response.data.items);
                        setDriveItemId(response.data.driveItemId);
                        setBreadcrumbs(response.data.breadcrumbs);
                        setTotalCount(response.data.totalCount);
                    }
                    setNextLink(response.data.nextLink);
                    setHasMore(response.data.hasMore);

                    // Update infinite scroll state based on hasMore
                    if (!response.data.hasMore) {
                        browseInfiniteScrollState.setComplete();
                    } else if (isLoadMore) {
                        browseInfiniteScrollState.finishLoading();
                    }
                }
            } catch (err) {
                const apiError = err as IApiError;
                const errorMessage = apiError.message || 'Failed to browse files';
                setError(errorMessage);

                if (isLoadMore) {
                    browseInfiniteScrollState.setError(errorMessage);
                } else {
                    // Fallback to empty state on error
                    setFiles([]);
                    setBreadcrumbs([]);
                }
            } finally {
                if (isLoadMore) {
                    setLoadingMore(false);
                } else {
                    setLoading(false);
                }
            }
        },
        [hierarchyId],
    );

    const searchItemsCallback = useCallback<IHookState['searchItems']>(
        async (query, isLoadMore, customNextLink, explicitPageSize) => {
            // Don't search if query is too short, unless it's a load more operation
            if (!isLoadMore && query.length < 3) return;

            if (isLoadMore) {
                setLoadingMoreSearch(true);
            } else {
                setLoading(true);
            }
            setError(null);

            try {
                const params: ISearchRequest = {
                    hierarchyId,
                    query,
                    pageSize: explicitPageSize ?? pageSizeRef.current,
                    nextLink: customNextLink,
                };

                const response = await searchItems(params);

                if (response.data) {
                    if (isLoadMore) {
                        // Append to existing search results
                        setSearchResults(prev => [...prev, ...response.data!.items]);
                    } else {
                        // Replace search results
                        setSearchResults(response.data.items);
                    }
                    setSearchNextLink(response.data.nextLink);
                    setSearchHasMore(response.data.hasMore);

                    // Update infinite scroll state based on hasMore
                    if (!response.data.hasMore) {
                        searchInfiniteScrollState.setComplete();
                    } else if (isLoadMore) {
                        searchInfiniteScrollState.finishLoading();
                    }
                }
            } catch (err) {
                const apiError = err as IApiError;
                const errorMessage = apiError.message || 'Failed to search files';
                setError(errorMessage);

                if (isLoadMore) {
                    searchInfiniteScrollState.setError(errorMessage);
                } else {
                    setSearchResults([]);
                }
            } finally {
                if (isLoadMore) {
                    setLoadingMoreSearch(false);
                } else {
                    setLoading(false);
                }
            }
        },
        [hierarchyId],
    );

    // Internal method for fetching items - used by both fetchItem and fetchItems
    const fetchItems = useCallback(
        async (cTagParsedIds: string[]): Promise<ISharePointItem[]> => {
            setLoading(true);
            setError(null);

            try {
                const params: IItemsRequest = {
                    hierarchyId,
                    cTagParsedIds,
                };

                const response = await getItems(params);

                if (response.data) {
                    return response.data;
                }

                const fetchError = new Error('No data received from items fetch');
                throw fetchError;
            } catch (err) {
                const apiError = err as IApiError;
                const errorMessage = apiError.message || 'Failed to fetch items';
                setError(errorMessage);
                throw new Error(errorMessage);
            } finally {
                setLoading(false);
            }
        },
        [hierarchyId],
    );

    const generateNodesCallback = useCallback<IHookState['generateNodes']>(
        async (targetHierarchyId, items) => {
            setLoading(true);
            setError(null);

            try {
                const params: IGenerateNodesRequest = {
                    hierarchyId: targetHierarchyId,
                    items,
                };

                const response = await generateNodes(params);

                if (response.data) {
                    return response.data;
                }

                throw new Error('No response data');
            } catch (err) {
                const apiError = err as IApiError;
                const errorMessage = apiError.message || 'Failed to generate nodes';
                setError(errorMessage);
                throw new Error(errorMessage);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const handleFolderOpen = useCallback<IHookState['handleFolderOpen']>(
        folder => {
            // Reset infinite scroll states when navigating
            browseInfiniteScrollState.reset();
            searchInfiniteScrollState.reset();

            // Navigate to the folder
            browserItemsCallback(folder.driveItemId);
        },
        [browserItemsCallback],
    );

    const handleBreadcrumbClick = useCallback<IHookState['handleBreadcrumbClick']>(
        index => {
            const targetNavigationItem = breadcrumbs[index];

            if (targetNavigationItem) {
                // Reset infinite scroll states when navigating
                browseInfiniteScrollState.reset();
                searchInfiniteScrollState.reset();

                // Navigate to the clicked folder
                browserItemsCallback(targetNavigationItem.driveItemId);
            }
        },
        [breadcrumbs, browserItemsCallback],
    );

    const reset = useCallback<IHookState['reset']>(() => {
        setLoading(false);
        setLoadingMore(false);
        setLoadingMoreSearch(false);
        setError(null);
        setFiles([]);
        setSearchResults([]);
        setBreadcrumbs([]);
        setDriveItemId(undefined);
        setTotalCount(undefined);
        setNextLink(undefined);
        setHasMore(false);
        setSearchNextLink(undefined);
        setSearchHasMore(false);
        browseInfiniteScrollState.reset();
        searchInfiniteScrollState.reset();
    }, []);

    return {
        // Data
        files,
        searchResults,
        breadcrumbs,
        driveItemId,
        totalCount,

        // States
        loading,
        loadingMore,
        loadingMoreSearch,
        error,

        // Pagination
        nextLink,
        hasMore,
        searchNextLink,
        searchHasMore,

        // Actions
        browserItems: browserItemsCallback,
        searchItems: searchItemsCallback,
        fetchItems,
        generateNodes: generateNodesCallback,

        // Navigation
        handleFolderOpen,
        handleBreadcrumbClick,

        // Infinite scroll
        browseInfiniteScrollState,
        searchInfiniteScrollState,

        // Utility
        reset,
    } satisfies IHookState;
};
